import { Policy } from '@commandDrivenStateEngine/core/policies';
import { AccountState } from '../state';
import { AccountCommand } from '../commands';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { uuidv7 } from 'uuidv7';

/**
 * Assignment Group Submit Policy (TV1-1878)
 *
 * Reacts to `assignmentGroups.pendingCommand` (set by the create/rename/delete
 * handlers — gated in invariants via the backend-operation capability). Issues
 * the matching generated client-library command(s), applies optimistic updates,
 * then clears the pending marker.
 *
 * Membership is managed INSIDE the create/edit form and applied here on save:
 * - create: CreateAssignmentGroup, then AddGroupMember for each selected member.
 * - rename: RenameAssignmentGroup (if name changed), then diff the selected
 *   members against the current group — AddGroupMember for additions and
 *   RemoveGroupMember for removals. Member calls run sequentially.
 *
 * The backend command bodies mirror the aggregate schemas:
 * - CreateAssignmentGroup / RenameAssignmentGroup: { name }
 * - DeleteAssignmentGroup: {}
 * - AddGroupMember / RemoveGroupMember: { userId }
 */
export const createGroupSubmitPolicy = (
  backend: ClientLibraryType,
  accountId: string
): Policy<AccountState, AccountCommand> => ({
  name: 'group-submit',

  shouldReact: ( prev, next ) =>
    prev.assignmentGroups.pendingCommand === null &&
    next.assignmentGroups.pendingCommand !== null,

  onStateChange: async ({ next, ctx }) => {
    const pending = next.assignmentGroups.pendingCommand;
    if ( !pending ) return;

    const finish = () => {
      ctx.dispatch(
        { type: 'account/groups/pending/clear', payload: {}},
        { source: 'api' }
      );
      ctx.dispatch(
        { type: 'account/groups/loading/setSubmitting', payload: { isLoading: false }},
        { source: 'api' }
      );
    };

    try {
      switch ( pending.kind ) {
        case 'create': {
          const groupId = uuidv7();
          await backend.commands.CreateAssignmentGroup({
            id: groupId,
            body: { name: pending.name }
          });
          // Apply the selected membership once and for all.
          for ( const userId of pending.memberUserIds ) {
            await backend.commands.AddGroupMember({ id: groupId, body: { userId }});
          }
          ctx.dispatch(
            {
              type: 'account/groups/data/upsert',
              payload: {
                group: { id: groupId, accountId, name: pending.name, memberUserIds: [...pending.memberUserIds]}
              }
            },
            { source: 'api' }
          );
          ctx.dispatch({ type: 'account/groups/form/close', payload: {}}, { source: 'api' });
          break;
        }
        case 'rename': {
          const group = next.assignmentGroups.list.find(( g ) => g.id === pending.groupId );
          // Rename only when the name actually changed (treat a missing group
          // as changed — defensively issue the rename).
          if ( !group || group.name !== pending.name ) {
            await backend.commands.RenameAssignmentGroup({
              id: pending.groupId,
              body: { name: pending.name }
            });
          }
          // Sync membership: add newly-selected, remove de-selected.
          const current = new Set( group?.memberUserIds ?? []);
          const selected = new Set( pending.memberUserIds );
          const toAdd = pending.memberUserIds.filter(( id ) => !current.has( id ));
          const toRemove = [...current].filter(( id ) => !selected.has( id ));
          for ( const userId of toAdd ) {
            await backend.commands.AddGroupMember({ id: pending.groupId, body: { userId }});
          }
          for ( const userId of toRemove ) {
            await backend.commands.RemoveGroupMember({ id: pending.groupId, body: { userId }});
          }
          if ( group ) {
            ctx.dispatch(
              {
                type: 'account/groups/data/upsert',
                payload: { group: { ...group, name: pending.name, memberUserIds: [...pending.memberUserIds]}}
              },
              { source: 'api' }
            );
          }
          ctx.dispatch({ type: 'account/groups/form/close', payload: {}}, { source: 'api' });
          break;
        }
        case 'delete': {
          await backend.commands.DeleteAssignmentGroup({ id: pending.groupId, body: {}});
          ctx.dispatch(
            { type: 'account/groups/data/remove', payload: { groupId: pending.groupId }},
            { source: 'api' }
          );
          break;
        }
      }
      finish();
    } catch ( error ) {
      console.error( '[account] Assignment group submit error:', error );
      finish();
      throw error;
    }
  }
});
